import { GanttColumn, GanttSortState } from './gantt.types';
import { compareGanttColumnValues, getGanttSortProperty, isGanttColumnSortable } from './gantt-column-values';
import { getColumnPropertyKey } from './gantt-remember';

/** Accept older single-column preferences and current ordered sort lists. */
export function normalizeGanttSort(value: unknown): GanttSortState[] {
    const values = Array.isArray(value) ? value : [value];
    const keys = new Set<string>();
    const sorts: GanttSortState[] = [];
    for (const item of values) {
        if (!item || typeof item !== 'object' || typeof item.propertyKey !== 'string' || !item.propertyKey
            || !['asc', 'desc'].includes(item.direction) || keys.has(item.propertyKey)) continue;
        keys.add(item.propertyKey);
        sorts.push({ propertyKey: item.propertyKey, direction: item.direction });
    }
    return sorts;
}

export function cycleGanttSort(sorts: GanttSortState[], propertyKey: string, additive: boolean): GanttSortState[] {
    const index = sorts.findIndex(sort => sort.propertyKey === propertyKey);
    const current = sorts[index];
    const next: GanttSortState | undefined = current?.direction === 'desc' ? undefined
        : { propertyKey, direction: current ? 'desc' : 'asc' };
    if (!additive) return next ? [next] : [];
    if (!next) return sorts.filter(sort => sort.propertyKey !== propertyKey);
    if (index < 0) return [...sorts, next];
    return sorts.map((sort, priority) => priority === index ? next : sort);
}

/** Retain the previous persisted shape when only one column is sorted. */
export function rememberGanttSort(sorts: GanttSortState[]): GanttSortState | GanttSortState[] | undefined {
    const copied = sorts.map(sort => ({ ...sort }));
    return copied.length > 1 ? copied : copied[0];
}

export function createGanttSortComparator(sorts: GanttSortState[], columns: GanttColumn[], loadedOrder: Map<string, number>) {
    // Resolve metadata once per sort, rather than looking up columns for every row pair.
    const rules = sorts.flatMap(sort => {
        const column = columns.find(item => getColumnPropertyKey(item) === sort.propertyKey);
        return column && isGanttColumnSortable(column) ? [{ sort, column, key: getGanttSortProperty(column) }] : [];
    });
    return (left: Record<string, unknown>, right: Record<string, unknown>): -1 | 0 | 1 => {
        for (const rule of rules) {
            const comparison = compareGanttColumnValues(left[rule.key], right[rule.key], rule.column, rule.sort.direction === 'desc');
            if (comparison) return Math.sign(comparison) as -1 | 0 | 1;
        }
        // Stable ties and an empty rule list both use the provider's loaded order.
        return Math.sign((loadedOrder.get(String(left['id'])) ?? loadedOrder.size)
            - (loadedOrder.get(String(right['id'])) ?? loadedOrder.size)) as -1 | 0 | 1;
    };
}
